import React, { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Search, Bell, Shield, Menu, X, LogOut, Settings, User, AlertTriangle } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import api from '../services/api'

const Navbar = () => {
  const { user, logout } = useAuth()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [alertCount, setAlertCount] = useState(0)
  const profileRef = useRef(null)

  useEffect(() => {
    const handleClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  useEffect(() => {
    api.get('/analytics/alerts').then(res => {
      if (res.data?.success) setAlertCount(res.data.data.summary?.total || 0)
    }).catch(() => {})
  }, [])

  const navItems = [
    { label: 'Dashboard', path: '/' },
    { label: 'Analytics', path: '/analytics' },
    { label: 'Scanner', path: '/scanner' },
    { label: 'Reports', path: '/reports' },
    { label: 'Heat Map', path: '/heatmap' },
    { label: 'Users', path: '/users' },
  ]

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 px-4 pt-4">
      <div className="max-w-7xl mx-auto bg-white/95 backdrop-blur-md rounded-2xl shadow-lg border border-gray-100/50 px-4 sm:px-6 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-2 shrink-0">
          <Shield className="h-7 w-7 text-[#1E4D40]" />
          <span className="text-lg font-bold text-gray-900">Verifi<span className="text-[#1E4D40]">AI</span></span>
        </Link>

        <div className="hidden md:flex items-center space-x-1 mx-4">
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                isActive(item.path)
                  ? 'bg-[#1E4D40] text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center space-x-3">
          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors hidden sm:block">
            <Search className="h-5 w-5" />
          </button>
          <Link to="/alerts" className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors relative">
            <Bell className="h-5 w-5" />
            {alertCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1">{alertCount > 9 ? '9+' : alertCount}</span>
            )}
          </Link>

          <div className="relative" ref={profileRef}>
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              className="flex items-center space-x-2 p-1 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <img
                src={user?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'U')}&background=1E4D40&color=fff`}
                alt="avatar"
                className="h-8 w-8 rounded-full"
              />
            </button>
            {profileOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>
                <Link to="/settings" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" onClick={() => setProfileOpen(false)}>
                  <Settings className="h-4 w-4 mr-2" /> Settings
                </Link>
                <button onClick={() => { logout(); setProfileOpen(false) }} className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </button>
              </div>
            )}
          </div>

          <button className="md:hidden p-2 text-gray-500 hover:bg-gray-100 rounded-xl" onClick={() => setMobileNavOpen(!mobileNavOpen)}>
            {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileNavOpen && (
        <div className="md:hidden mt-2 max-w-7xl mx-auto bg-white rounded-2xl shadow-lg border border-gray-100 p-3">
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileNavOpen(false)}
              className={`block px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                isActive(item.path) ? 'bg-[#1E4D40] text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </nav>
  )
}

export default Navbar
